import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../StuStyles/TrainingPrograms.css";
import ApiService from "../../../nhrc_apis/ApiService"; // ✅ your existing ApiService

import img1 from "../../../assets/stu-live-event1.png";
import img2 from "../../../assets/stu-live-event2.png";
import img3 from "../../../assets/stu-live-event3.png";
import img4 from "../../../assets/stu-live-event4.png";

// Fallback images in case API image is missing
const FALLBACK_IMAGES = [img1, img2, img3, img4];

// How many cards to show per page
const CARDS_PER_PAGE = 3;

// 🔴 IMPORTANT: Set this to your backend's base URL (no trailing slash)
// e.g. "http://localhost:8000" or "https://api.nhrclub.com"
const API_BASE_URL = ApiService.baseURL || "http://localhost:8000";

// Builds a full image URL from a relative path like "/uploads/xyz.png"
const buildImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
};

// Formats "09:00:00" -> "09:00 AM"
const formatTime = (timeStr) => {
  if (!timeStr) return "—";
  const [h, m] = timeStr.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${m} ${ampm}`;
};

// Formats "2026-06-07" -> "07 Jun 2026"
const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const TrainingPrograms = () => {
  const navigate = useNavigate();

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ─── PAGINATION STATE ───────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await ApiService.TrainingAPI.getStudentTrainings();
        const data = response.data;

        // Normalize API response — adjust field names to match your backend
        const normalized = Array.isArray(data) ? data : data?.content || data?.programs || [];

        const mapped = normalized.map((item, index) => {
          const startDate = formatDate(item.start_date);
          const endDate = formatDate(item.end_date);
          const startTime = formatTime(item.start_time);
          const endTime = formatTime(item.end_time);

          return {
            id: item.id ?? item._id ?? index,
            title: item.title || item.programTitle || "Training Program",
            category: item.program_category || item.category || "General",
            host: item.trainer_name || item.host || item.trainerName || "—",
            mode: item.training_mode || item.mode || item.eventmode || "—",
            schedule:
              item.start_date && item.end_date
                ? startDate === endDate
                  ? startDate
                  : `${startDate} – ${endDate}`
                : "—",
            time:
              item.start_time && item.end_time
                ? `${startTime} – ${endTime}`
                : "—",
            location: item.location || item.trainingLocation || "—",
            capacity: item.capacity ?? "—",
            contactEmail: item.contact_email || "—",
            status: item.status || "—",
            description: item.short_description || "",
            image:
              buildImageUrl(item.banner_image || item.image || item.imageUrl) ||
              FALLBACK_IMAGES[index % 4],
          };
        });

        setPrograms(mapped);
        setCurrentPage(1); // reset to first page whenever fresh data loads

      } catch (err) {
        console.error("Training fetch error:", err);
        setError("Failed to load training programs. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchPrograms();
  }, []);

  const handleApply = (program) => {
    navigate("/student/ApplicationForm", { state: program });
  };

  // ─── PAGINATION LOGIC ───────────────────────────────────
  const totalPages = Math.ceil(programs.length / CARDS_PER_PAGE);

  const startIndex = (currentPage - 1) * CARDS_PER_PAGE;
  const endIndex = startIndex + CARDS_PER_PAGE;
  const currentPrograms = programs.slice(startIndex, endIndex);

  const handlePrev = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  // ─── LOADING STATE ──────────────────────────────────────
  if (loading) {
    return (
      <div className="stu-live-container">
        <h2 className="stu-live-title">Live Training Program</h2>
        <div className="stu-live-loader">
          <p>Loading programs, please wait...</p>
        </div>
      </div>
    );
  }

  // ─── ERROR STATE ────────────────────────────────────────
  if (error) {
    return (
      <div className="stu-live-container">
        <h2 className="stu-live-title">Live Training Program</h2>
        <div className="stu-live-error">
          <p>{error}</p>
          <button
            className="stu-live-btn"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ─── EMPTY STATE ────────────────────────────────────────
  if (programs.length === 0) {
    return (
      <div className="stu-live-container">
        <h2 className="stu-live-title">Live Training Program</h2>
        <p className="stu-live-empty">No training programs available right now.</p>
      </div>
    );
  }

  // ─── MAIN RENDER ────────────────────────────────────────
  return (
    <div className="stu-live-container">
      <h2 className="stu-live-title">Live Training Program</h2>

      <div className="stu-live-wrapper">
        {currentPrograms.map((item, index) => (
          <div className="stu-live-card" key={item.id ?? index}>

            <div className="stu-live-left">
              <h3 className="stu-live-heading">{item.title}</h3>

              <p className="stu-live-company">
                Program Category : <span>{item.category}</span>
              </p>
              <p className="stu-live-text">
                Trainer / Host : <span>{item.host}</span>
              </p>
              <p className="stu-live-text">
                Training Mode : <span>{item.mode}</span>
              </p>
              <p className="stu-live-text">
                Schedule : <span className="stu-age">{item.schedule}</span>
              </p>
              <p className="stu-live-text">
                Time : <span className="stu-age">{item.time}</span>
              </p>
              <p className="stu-live-text">
                Location : <span className="stu-age">{item.location}</span>
              </p>
              <p className="stu-live-text">
                Seats : <span className="stu-age">{item.capacity}</span>
              </p>

              <p className="stu-live-seats">⚡ Limited Seats Available!</p>

              <button
                className="stu-live-btn"
                onClick={() => handleApply(item)}
              >
                REGISTER NOW
              </button>
            </div>

            <div className="stu-live-right">
              <img
                src={item.image}
                alt={item.title}
                className="stu-live-img"
                onError={(e) => {
                  e.target.onerror = null; // prevent infinite loop
                  e.target.src = FALLBACK_IMAGES[index % 4];
                }}
              />
            </div>

          </div>
        ))}
      </div>

      {/* ─── PAGINATION CONTROLS ───────────────────────── */}
      {totalPages > 1 && (
        <div className="stu-live-pagination">
          <button
            className="stu-live-page-btn"
            onClick={handlePrev}
            disabled={currentPage === 1}
          >
            ← Previous
          </button>

          <span className="stu-live-page-info">
            Page {currentPage} of {totalPages}
          </span>

          <button
            className="stu-live-page-btn"
            onClick={handleNext}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default TrainingPrograms;